import { useEffect, useState } from 'react'
import { KIND_LABEL, type EditableSlide, type SlideKind, type SlideSpec } from '../../lib/types'
import { seriesOf, seriesStyle } from '../../lib/series'

const EMOJI_PRESETS = ['👏', '🔥', '😂', '❤️', '😮', '🤔', '👍', '🎉', '🤯', '😴', '🙌', '💯']

/**
 * Форма слайда — одна и та же для трёх мест: создания и правки в живом эфире
 * (пульт) и редактора демонстрации. Знает только форму слайда, а не то, куда он
 * отправится: пульт шлёт по вебсокету, редактор кладёт в демонстрацию.
 *
 * При правке в эфире важно помнить серверное правило: переименование варианта
 * голоса не отменяет, а изменение их числа или отметок «верный» обнуляет
 * ответы — в эфире про это сказано в подсказке, в демонстрации ответов просто нет.
 */
export function SlideForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial: EditableSlide | null
  onSubmit: (spec: SlideSpec) => void
  onCancel?: () => void
}) {
  const [kind, setKind] = useState<SlideKind>(initial?.kind ?? 'POLL_SINGLE')
  const [title, setTitle] = useState(initial?.title ?? '')
  const [options, setOptions] = useState<{ text: string; correct: boolean }[]>(
    initial && initial.options.length > 0
      ? initial.options.map((o) => ({ text: o.text, correct: o.correct === true }))
      : [
          { text: '', correct: false },
          { text: '', correct: false },
        ],
  )
  const [timer, setTimer] = useState<number | null>(initial ? initial.timerSeconds : 30)
  const [maxLength, setMaxLength] = useState(initial?.maxTextLength ?? 140)
  const [moderated, setModerated] = useState(initial?.moderated ?? false)
  const [emojiSet, setEmojiSet] = useState<string[]>(
    initial?.emojiSet ?? ['👏', '🔥', '😂', '❤️', '😮', '🤔'],
  )
  const [cloudView, setCloudView] = useState(initial ? initial.resultView === 'CLOUD' : true)

  const isPoll = kind === 'POLL_SINGLE' || kind === 'POLL_MULTI'
  const isEdit = initial !== null

  // Эмодзи-шторм — фоновая реакция зала, а не вопрос с дедлайном: по умолчанию
  // пусть живёт, пока ведущий не закроет сам. При правке чужой выбор не трогаем.
  useEffect(() => {
    if (isEdit) return
    setTimer(kind === 'EMOJI_STORM' ? null : 30)
  }, [kind, isEdit])

  const submit = () => {
    if (!title.trim()) return
    const spec: SlideSpec = { kind, title: title.trim(), timerSeconds: timer }

    if (isPoll) {
      const filled = options.filter((o) => o.text.trim())
      if (filled.length < 2) return
      spec.options = filled
    }
    if (kind === 'FREE_TEXT') {
      spec.maxTextLength = maxLength
      spec.moderated = moderated
      spec.resultView = cloudView ? 'CLOUD' : 'BARS'
    }
    if (kind === 'EMOJI_STORM') {
      spec.emojiSet = emojiSet
    }
    onSubmit(spec)

    if (!isEdit) {
      setTitle('')
      setOptions([
        { text: '', correct: false },
        { text: '', correct: false },
      ])
    }
  }

  // Для слайда с одним правильным ответом отметка «верный» должна быть одна.
  const markCorrect = (index: number, value: boolean) => {
    setOptions((current) =>
      current.map((option, i) => ({
        ...option,
        correct: kind === 'POLL_SINGLE' ? (i === index ? value : false) : i === index ? value : option.correct,
      })),
    )
  }

  return (
    <div className="card col">
      <div className="row spread">
        <h3 className="card-title">{isEdit ? 'Правка слайда' : 'Новый слайд'}</h3>
        {onCancel && (
          <button className="link-button muted tiny" onClick={onCancel}>
            отменить
          </button>
        )}
      </div>

      <select value={kind} onChange={(e) => setKind(e.target.value as SlideKind)} aria-label="Тип слайда">
        {Object.entries(KIND_LABEL).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>

      <input
        placeholder="Вопрос или заголовок"
        value={title}
        maxLength={200}
        onChange={(e) => setTitle(e.target.value)}
      />

      {isPoll && (
        <div className="col" style={{ gap: 8 }}>
          {options.map((option, index) => (
            <div className="row" key={index} style={{ gap: 8 }}>
              {/* Та же буква и тот же цвет, что увидит зал. */}
              <span className="form-letter" style={seriesStyle(index)} aria-hidden>
                {seriesOf(index).letter}
              </span>
              <input
                className="grow"
                placeholder={`Вариант ${index + 1}`}
                value={option.text}
                onChange={(e) =>
                  setOptions((current) =>
                    current.map((o, i) => (i === index ? { ...o, text: e.target.value } : o)),
                  )
                }
              />
              <button
                // Зелёная, а не коралловая: коралл на экране — это главное действие,
                // а здесь отметка «верный ответ», и цвет у неё тот же, что у ✓ на проекторе.
                className={`btn check-toggle${option.correct ? ' check-toggle-on' : ''}`}
                title="Отметить верным"
                aria-pressed={option.correct}
                onClick={() => markCorrect(index, !option.correct)}
              >
                ✓
              </button>
            </div>
          ))}
          <div className="row">
            <button
              className="btn btn-ghost tiny"
              onClick={() => setOptions((c) => [...c, { text: '', correct: false }])}
            >
              + вариант
            </button>
            {options.length > 2 && (
              <button className="btn btn-ghost tiny" onClick={() => setOptions((c) => c.slice(0, -1))}>
                − убрать
              </button>
            )}
          </div>
          <p className="muted tiny" style={{ margin: 0 }}>
            Отметьте галочкой верные варианты — тогда слайд даёт баллы. Не отметили — обычный опрос
            без правильного ответа.
          </p>
        </div>
      )}

      {kind === 'FREE_TEXT' && (
        <div className="col" style={{ gap: 8 }}>
          <label className="col tiny" style={{ gap: 4 }}>
            Ограничение длины: {maxLength}
            <input
              type="range"
              min={20}
              max={280}
              step={10}
              value={maxLength}
              onChange={(e) => setMaxLength(Number(e.target.value))}
            />
          </label>
          <label className="row tiny" style={{ gap: 8 }}>
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={cloudView}
              onChange={(e) => setCloudView(e.target.checked)}
            />
            Показывать облаком (иначе списком)
          </label>
          <label className="row tiny" style={{ gap: 8 }}>
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={moderated}
              onChange={(e) => setModerated(e.target.checked)}
            />
            Премодерация: ответы попадают на экран после вашего аппрува
          </label>
        </div>
      )}

      {kind === 'EMOJI_STORM' && (
        <div className="col" style={{ gap: 8 }}>
          <span className="muted tiny">Что можно тапать</span>
          <div className="row row-wrap" style={{ gap: 6 }}>
            {EMOJI_PRESETS.map((emoji) => {
              const on = emojiSet.includes(emoji)
              return (
                <button
                  key={emoji}
                  className={`btn${on ? ' btn-primary' : ' btn-ghost'}`}
                  style={{ padding: '8px 10px', fontSize: 20 }}
                  onClick={() =>
                    setEmojiSet((current) =>
                      on ? current.filter((e) => e !== emoji) : [...current, emoji].slice(0, 12),
                    )
                  }
                >
                  {emoji}
                </button>
              )
            })}
          </div>
        </div>
      )}

      <label className="col tiny" style={{ gap: 4 }}>
        Таймер
        <select
          value={timer ?? 'none'}
          onChange={(e) => setTimer(e.target.value === 'none' ? null : Number(e.target.value))}
        >
          <option value="none">без таймера — закрою кнопкой</option>
          {[10, 15, 20, 30, 45, 60, 90, 120].map((seconds) => (
            <option key={seconds} value={seconds}>
              {seconds} секунд
            </option>
          ))}
        </select>
      </label>

      <button className="btn btn-primary" onClick={submit} disabled={!title.trim()}>
        {isEdit ? 'Сохранить слайд' : 'Добавить слайд'}
      </button>

      {isEdit && (
        <p className="muted tiny" style={{ margin: 0 }}>
          В живом эфире переименование варианта сохраняет поданные голоса, а изменение их числа или
          отметок «верный» обнулит ответы и снимет баллы за этот слайд — иначе они относились бы к
          другому вопросу.
        </p>
      )}
    </div>
  )
}
